/**
 * Official Hubwayz / Mishor Book request builder (docs.hubwayz.com).
 *
 * Shape: paymentMethod + services[].{ searchRequest, bookingRequest[{ code, token, pax }] }
 *
 * Wired via runHubwayzOfficialBookHold → FlightHotelPaymentPage (Phase 2H).
 * Supplier Book execution remains hard-blocked by hubwayzBookExecutionGuard.
 * Does not call supplier APIs from this module.
 *
 * Phase 2O: customer.contact.phone is required (Mishor a205). Docs FAQ:
 * all customer contact fields mandatory; unknown phone/zip may be "N/A".
 */

import { isFlightPassengerDdMmYyyy } from "../../../Components/FlightBookingFlowMockup/flightBookingMockup/flightPassengersFormShared";
import { roomInfoFromAppliedSearch } from "../buildUnifiedPaxAndHubwayzLead";

const SUPPORTED_PAYMENT_METHODS = new Set([
  "account_credit",
  "credit_card",
  "enett",
]);

/** Mishor accepts "N/A" or a dialable string; reject empty / whitespace-only. */
const PHONE_NA = "N/A";
const PHONE_OK =
  /^(N\/A|\+?\d[\d\s().-]{5,}\d)$/i;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function ddMmYyyyToIsoStrict(raw) {
  const s = String(raw || "").trim();
  if (!isFlightPassengerDdMmYyyy(s)) return null;
  const [d, m, y] = s.split("/");
  return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
}

/** Completed years of an ISO birth date on an ISO calendar day (check-in). */
export function ageAtIsoDate(isoBirth, isoOn) {
  if (!ISO_DATE.test(String(isoBirth || "")) || !ISO_DATE.test(String(isoOn || ""))) {
    return null;
  }
  const [y, m, d] = String(isoBirth).split("-").map(Number);
  const [Y, M, D] = String(isoOn).split("-").map(Number);
  let age = Y - y;
  if (M < m || (M === m && D < d)) age -= 1;
  return age;
}

function isoShiftYears(iso, deltaYears) {
  const [y, m, d] = String(iso).split("-").map(Number);
  const year = y + deltaYears;
  const lastDay = new Date(Date.UTC(year, m, 0)).getUTCDate();
  const day = Math.min(d, lastDay);
  return `${year}-${String(m).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function isoAddDays(iso, days) {
  const [y, m, d] = String(iso).split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + days));
  const year = dt.getUTCFullYear();
  const month = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const day = String(dt.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const CUSTOMER_AGE_YEARS = 30;

function todayIsoUtc(now = new Date()) {
  const year = now.getUTCFullYear();
  const month = String(now.getUTCMonth() + 1).padStart(2, "0");
  const day = String(now.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Hidden booking-customer birthDate: anchor minus 30 years, same month and day.
 * Anchor is check-in when it is ISO, otherwise today. Feb 29 clamps to Feb 28.
 * Not shown in the guest form and not stored as a traveler DOB.
 */
export function derivedCustomerBirthDate(checkIn, todayIso) {
  const on = String(checkIn || "").trim();
  const today = String(todayIso || "").trim();
  const anchor = ISO_DATE.test(on)
    ? on
    : ISO_DATE.test(today)
      ? today
      : todayIsoUtc();
  return isoShiftYears(anchor, -CUSTOMER_AGE_YEARS);
}

/**
 * Calendar date of the Book request in UTC.
 * Hubwayz docs say child age is calculated on the booking date and do not
 * name a timezone. This product uses the UTC date only, never Israel-local.
 */
export function bookingDateIsoUtc(now = new Date()) {
  return todayIsoUtc(now);
}

/**
 * Hidden supplier date for a search child age when the guest did not type a DOB.
 * Same month and day as the booking date, `age` years earlier. Feb 29 clamps to Feb 28.
 * Not anchored to check-in.
 */
export function derivedChildBirthDate(bookingDate, age) {
  const on = String(bookingDate || "").trim();
  const years = Number(age);
  if (!ISO_DATE.test(on) || !Number.isInteger(years) || years < 0 || years > 17) {
    return null;
  }
  return isoShiftYears(on, -years);
}

/** Inclusive DOB window for a completed age on the booking date. */
export function childDobRangeForAge(bookingDate, age) {
  const on = String(bookingDate || "").trim();
  const years = Number(age);
  if (!ISO_DATE.test(on) || !Number.isInteger(years) || years < 0) return null;
  const until = isoShiftYears(on, -years);
  const from = isoAddDays(isoShiftYears(on, -(years + 1)), 1);
  return { from, until };
}

function suppliedChildBirthIso(child) {
  const raw = String(child?.dateOfBirth || child?.birthDate || "").trim();
  if (!raw) return null;
  if (ISO_DATE.test(raw)) return raw;
  return ddMmYyyyToIsoStrict(raw);
}

function resolveBookingDate(input = {}) {
  const explicit = String(input.bookingDate || "").trim();
  if (ISO_DATE.test(explicit)) return explicit;
  const now = input.now instanceof Date ? input.now : new Date();
  return bookingDateIsoUtc(now);
}

export function formatIsoDateDotted(iso) {
  if (!ISO_DATE.test(String(iso || ""))) return String(iso || "");
  const [y, m, d] = String(iso).split("-");
  return `${d}.${m}.${y}`;
}

function fail(code, message, details = null) {
  return {
    ok: false,
    error: { code, message, details },
    payload: null,
  };
}

function ok(payload, meta = {}) {
  return { ok: true, error: null, payload, meta };
}

/**
 * Normalize / validate Hubwayz contact.phone (customer + lead).
 * @returns {{ ok: true, phone: string } | { ok: false, code: string, message: string }}
 */
export function normalizeHubwayzContactPhone(raw) {
  const phone = String(raw ?? "").trim();
  if (!phone) {
    return {
      ok: false,
      code: "missing_customer_contact_phone",
      message:
        "customer.contact.phone is required (Mishor a205). Use authorized TEST phone or N/A.",
    };
  }
  if (!PHONE_OK.test(phone)) {
    return {
      ok: false,
      code: "invalid_customer_contact_phone",
      message:
        "customer.contact.phone must be N/A or an international/local dial string",
      details: { phonePreview: phone.slice(0, 4) + "…" },
    };
  }
  return { ok: true, phone: phone === PHONE_NA ? PHONE_NA : phone };
}

/**
 * Build mandatory customer.contact per docs FAQ (all fields required).
 */
export function buildHubwayzCustomerContact(raw = {}) {
  const email = String(raw.email || "").trim();
  const phoneCheck = normalizeHubwayzContactPhone(raw.phone);
  if (!phoneCheck.ok) {
    return { ok: false, error: phoneCheck };
  }
  const country = String(raw.country || "IL")
    .trim()
    .toUpperCase()
    .slice(0, 2);
  if (!country || country.length !== 2) {
    return {
      ok: false,
      error: {
        code: "missing_customer_contact_country",
        message: "customer.contact.country (ISO-2) is required",
      },
    };
  }
  if (!email) {
    return {
      ok: false,
      error: {
        code: "missing_customer_contact_email",
        message: "customer.contact.email is required",
      },
    };
  }
  return {
    ok: true,
    contact: {
      email,
      phone: phoneCheck.phone,
      country,
      address: String(raw.address || "N/A").trim() || "N/A",
      city: String(raw.city || "N/A").trim() || "N/A",
      state: String(raw.state || "N/A").trim() || "N/A",
      zip: String(raw.zip || "00000").trim() || "00000",
    },
  };
}

function asAdultGuest(p, { lead = false, includeContact = false } = {}) {
  const title =
    String(p.gender || p.title || "M").toUpperCase() === "F" ? "F" : "M";
  const first = String(p.firstName || p.name?.first || "").trim();
  const last = String(p.lastName || p.name?.last || "").trim();
  if (!first || !last) return null;
  const guest = {
    title,
    lead: !!lead,
    name: { first, last },
  };
  if (includeContact || lead) {
    const email = String(p.email || p.contact?.email || "").trim();
    const phoneRaw = String(p.cell || p.phone || p.contact?.phone || "").trim();
    const phoneCheck = normalizeHubwayzContactPhone(phoneRaw);
    if (!phoneCheck.ok) {
      return { __invalid: true, reason: phoneCheck.code, message: phoneCheck.message };
    }
    const country = String(p.nationality || p.contact?.country || "IL")
      .trim()
      .toUpperCase()
      .slice(0, 2);
    guest.contact = {
      email: email || undefined,
      phone: phoneCheck.phone,
      country: country || "IL",
      address: p.address || p.contact?.address || "N/A",
      city: p.city || p.contact?.city || "N/A",
      state: p.state || p.contact?.state || "N/A",
      zip: p.zip || p.contact?.zip || "00000",
    };
  }
  return guest;
}

function asChildGuest(p, birthDate) {
  const first = String(p.firstName || "").trim();
  const last = String(p.lastName || "").trim();
  if (!first || !last) return { ok: false, reason: "child_name" };
  if (!ISO_DATE.test(String(birthDate || ""))) {
    return { ok: false, reason: "missing_child_search_age" };
  }
  return {
    ok: true,
    guest: {
      title: "C",
      birthDate,
      name: { first, last },
    },
  };
}

function childAgesFromPaxEntry(entry) {
  const raw = entry?.children;
  if (!Array.isArray(raw)) return null;
  return raw.map((c) => {
    if (typeof c === "number" && Number.isFinite(c)) return c;
    if (c && typeof c === "object") {
      const age = Number(c.age ?? c.Age);
      return Number.isFinite(age) ? age : c?.age;
    }
    const n = Number(c);
    return Number.isFinite(n) ? n : c;
  });
}

function stableRoomId(node) {
  if (!node || typeof node !== "object") return null;
  const raw = node.roomId ?? node.room_id;
  if (raw == null) return null;
  const text = String(raw).trim();
  return text || null;
}

function normalizedAgeList(childAges) {
  const ages = Array.isArray(childAges) ? childAges : [];
  return ages.map((age) => {
    if (age && typeof age === "object") {
      const n = Number(age.age ?? age.Age);
      return Number.isFinite(n) ? n : age.age;
    }
    const n = Number(age);
    return Number.isFinite(n) ? n : age;
  });
}

/**
 * Occupancy identity: adult count, child count, child ages sorted.
 * A 16-year-old stays a child. Ages are not moved between adults and children.
 */
export function occupancySignature(adults, childAges, childCount = null) {
  const adultCount = Number(adults);
  const ages = normalizedAgeList(childAges);
  const count = childCount == null ? ages.length : Number(childCount);
  const sorted = [...ages].sort((a, b) => {
    const na = Number(a);
    const nb = Number(b);
    if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return na - nb;
    const sa = String(a);
    const sb = String(b);
    if (sa < sb) return -1;
    if (sa > sb) return 1;
    return 0;
  });
  const adultsKey = Number.isFinite(adultCount) ? String(adultCount) : "na";
  const countKey = Number.isFinite(count) ? String(count) : "na";
  return {
    adults: Number.isFinite(adultCount) ? adultCount : null,
    childCount: Number.isFinite(count) ? count : null,
    childAges: sorted,
    key: `${adultsKey}|${countKey}|${sorted.map((age) => String(age)).join(",")}`,
  };
}

function readRoomOccupancy(room) {
  const pax = room?.pax && typeof room.pax === "object" ? room.pax : null;
  const adults = pax ? pax.adults : room?.adults;
  let childAges = [];
  if (pax && Array.isArray(pax.childAges)) childAges = pax.childAges;
  else if (pax && Array.isArray(pax.children)) childAges = pax.children;
  else if (Array.isArray(room?.childAges)) childAges = room.childAges;
  else if (Array.isArray(room?.children)) childAges = room.children;
  const declared = pax ? Number(pax.childrenCount) : Number(room?.childCount);
  const ages = normalizedAgeList(childAges);
  const childCount = Number.isFinite(declared)
    ? Math.max(declared, ages.length)
    : ages.length;
  return {
    adults,
    childAges: ages,
    childCount,
    roomId: stableRoomId(room) || stableRoomId(pax),
    signature: occupancySignature(adults, ages, childCount),
  };
}

/**
 * Pair each supplier room with exactly one requested room.
 * Supplier order is unchanged. Ties keep relative request order unless both
 * sides carry a stable room id, in which case the id must match one-to-one.
 */
export function mapRequestedRoomsToSupplierRooms(requestedRooms, supplierRooms) {
  const requested = Array.isArray(requestedRooms) ? requestedRooms : null;
  const supplier = Array.isArray(supplierRooms) ? supplierRooms : null;
  if (!requested || !supplier) {
    return {
      ok: false,
      code: "room_occupancy_map_impossible",
      message: "Requested room occupancy does not match supplier room occupancy",
      details: null,
    };
  }
  const req = requested.map((room, index) => ({
    index,
    ...readRoomOccupancy(room),
  }));
  const sup = supplier.map((room, index) => ({
    index,
    ...readRoomOccupancy(room),
  }));
  if (req.length !== sup.length) {
    return {
      ok: false,
      code: "room_occupancy_map_impossible",
      message: "Requested room occupancy does not match supplier room occupancy",
      details: { requestedRooms: req.length, supplierRooms: sup.length },
    };
  }

  const groups = new Map();
  for (const room of req) {
    if (!groups.has(room.signature.key)) {
      groups.set(room.signature.key, { requested: [], supplier: [] });
    }
    groups.get(room.signature.key).requested.push(room);
  }
  for (const room of sup) {
    const group = groups.get(room.signature.key);
    if (!group) {
      return {
        ok: false,
        code: "room_occupancy_map_impossible",
        message: "Requested room occupancy does not match supplier room occupancy",
        details: {
          supplierIndex: room.index,
          supplierSignature: room.signature.key,
          requestedSignatures: req.map((item) => item.signature.key),
        },
      };
    }
    group.supplier.push(room);
  }

  const supplierToRequested = new Array(sup.length);
  for (const [key, group] of groups) {
    if (group.requested.length !== group.supplier.length) {
      return {
        ok: false,
        code: "room_occupancy_map_impossible",
        message: "Requested room occupancy does not match supplier room occupancy",
        details: {
          signature: key,
          requested: group.requested.length,
          supplier: group.supplier.length,
        },
      };
    }
    const paired = pairEqualOccupancyRooms(group.requested, group.supplier, key);
    if (!paired.ok) return paired;
    for (const pair of paired.pairs) {
      supplierToRequested[pair.supplierIndex] = pair.requestedIndex;
    }
  }
  return { ok: true, supplierToRequested };
}

function pairEqualOccupancyRooms(requested, supplier, signature) {
  if (requested.length === 1) {
    return {
      ok: true,
      pairs: [
        {
          supplierIndex: supplier[0].index,
          requestedIndex: requested[0].index,
        },
      ],
    };
  }
  const requestIds = requested.map((room) => room.roomId);
  const supplierIds = supplier.map((room) => room.roomId);
  const requestHasId = requestIds.some(Boolean);
  const supplierHasId = supplierIds.some(Boolean);
  if (!requestHasId && !supplierHasId) {
    return {
      ok: true,
      pairs: supplier.map((room, index) => ({
        supplierIndex: room.index,
        requestedIndex: requested[index].index,
      })),
    };
  }
  const requestComplete =
    requestIds.every(Boolean) && new Set(requestIds).size === requestIds.length;
  const supplierComplete =
    supplierIds.every(Boolean) && new Set(supplierIds).size === supplierIds.length;
  const sameIds =
    requestComplete &&
    supplierComplete &&
    requestIds.every((id) => supplierIds.includes(id));
  if (!sameIds) {
    return {
      ok: false,
      code: "room_occupancy_map_ambiguous",
      message: "Identical room occupancies cannot be matched one-to-one",
      details: { signature },
    };
  }
  const requestedById = new Map(requested.map((room) => [room.roomId, room]));
  return {
    ok: true,
    pairs: supplier.map((room) => ({
      supplierIndex: room.index,
      requestedIndex: requestedById.get(room.roomId).index,
    })),
  };
}

function orderChildrenBySupplierAges(children, expectedAges, supplierAges) {
  if (!children.length) return [];
  const ages = supplierAges.length ? supplierAges : expectedAges;
  if (ages.length !== children.length || expectedAges.length !== children.length) {
    return null;
  }
  const used = new Set();
  const ordered = [];
  for (const age of ages) {
    let found = -1;
    for (let index = 0; index < expectedAges.length; index++) {
      if (used.has(index)) continue;
      const expected = Number(expectedAges[index]);
      const target = Number(age);
      if (Number.isFinite(expected) && Number.isFinite(target) && expected === target) {
        found = index;
        break;
      }
    }
    if (found < 0) return null;
    used.add(found);
    ordered.push({ child: children[found], expected: expectedAges[found] });
  }
  return ordered;
}

/**
 * Occupancy the guest form and Book must share with Search / Pre-Book.
 * searchRequest.pax is the array Pre-Book already sent. It wins over a
 * divergent appliedSearch count (for example room 1 inflated to 3).
 * A missing children key keeps that room's appliedSearch ages.
 */
export function approvedGuestRooms(searchRequest, appliedSearch, paxFallback = 2) {
  const fallback = roomInfoFromAppliedSearch(appliedSearch, paxFallback).rooms || [];
  const pax = searchRequest?.pax;
  if (!Array.isArray(pax) || !pax.length) {
    return fallback.map((r, index) => ({
      adults: r.adults,
      childAges: Array.isArray(r.kidsAges) ? r.kidsAges : [],
      roomId: stableRoomId(appliedSearch?.rooms?.[index]),
    }));
  }
  return pax.map((entry, index) => {
    const adultsNum = Number(entry?.adults);
    const adults =
      Number.isFinite(adultsNum) && adultsNum >= 1 ? adultsNum : 1;
    const fromPax = childAgesFromPaxEntry(entry);
    const fallbackAges = Array.isArray(fallback[index]?.kidsAges)
      ? fallback[index].kidsAges
      : [];
    return {
      adults,
      childAges: fromPax == null ? fallbackAges : fromPax,
      roomId: stableRoomId(entry) || stableRoomId(appliedSearch?.rooms?.[index]),
    };
  });
}

function roomIndexFromPassengerId(passenger) {
  const match = String(passenger?.id || "").match(/^r(\d+)_/);
  return match ? Number(match[1]) : null;
}

/**
 * Split passengers into the Search/Pre-Book room split.
 * Ids `r{room}_…` stay on that room. Untagged guests fill remaining
 * capacity in index order and never overflow into the next room.
 */
export function assignPassengersToRooms(
  passengers,
  appliedSearch,
  paxFallback = 2,
  searchRequest = null
) {
  const list = Array.isArray(passengers) ? passengers : [];
  const approved = approvedGuestRooms(searchRequest, appliedSearch, paxFallback);
  const roomInfo = roomInfoFromAppliedSearch({ rooms: approved }, paxFallback);
  const roomsSpec = roomInfo.rooms || [];
  const adults = list.filter((p) => p.role !== "child" && p.role !== "infant");
  const children = list.filter(
    (p) => p.role === "child" || p.role === "infant"
  );

  const rooms = roomsSpec.map((spec, index) => ({
    adults: [],
    children: [],
    expectedAdults: Math.max(1, Number(spec.adults) || 1),
    expectedChildren: Math.max(0, Number(spec.kids) || 0),
    expectedChildAges: Array.isArray(spec.kidsAges) ? spec.kidsAges.map(Number) : [],
    roomId: stableRoomId(approved[index]),
  }));

  const untaggedAdults = [];
  for (const adult of adults) {
    const roomIndex = roomIndexFromPassengerId(adult);
    const room = roomIndex != null ? rooms[roomIndex] : null;
    if (room) room.adults.push(adult);
    else untaggedAdults.push(adult);
  }
  for (const room of rooms) {
    while (room.adults.length < room.expectedAdults && untaggedAdults.length) {
      room.adults.push(untaggedAdults.shift());
    }
  }

  const untaggedChildren = [];
  const misplacedChildren = [];
  for (const child of children) {
    const roomIndex = roomIndexFromPassengerId(child);
    const room = roomIndex != null ? rooms[roomIndex] : null;
    if (!room) {
      untaggedChildren.push(child);
    } else if (room.children.length < room.expectedChildren) {
      room.children.push(child);
    } else {
      misplacedChildren.push(child);
    }
  }
  for (const room of rooms) {
    while (room.children.length < room.expectedChildren && untaggedChildren.length) {
      room.children.push(untaggedChildren.shift());
    }
  }
  return {
    rooms,
    leftoverAdults: untaggedAdults,
    leftoverChildren: [...untaggedChildren, ...misplacedChildren],
  };
}

function extractEnabledPaymentMethods(prebookRaw) {
  const root = prebookRaw?.content || prebookRaw?.data?.content || prebookRaw;
  const methods = root?.PaymentMethods || root?.paymentMethods;
  if (!Array.isArray(methods)) return null;
  return methods
    .filter((m) => String(m?.status || "").toLowerCase() === "enabled")
    .map((m) => String(m?.methodName || "").trim())
    .filter(Boolean);
}

/**
 * Build official Book payload.
 *
 * @param {object} input
 * @param {string} input.offerCode — search/poll offer code
 * @param {string} input.token — Pre-Book token
 * @param {object} input.searchRequest — original Mishor search body
 * @param {object} input.prebookNormalized — from normalizeHubwayzPrebook
 * @param {object} [input.prebookRaw] — raw Pre-Book for PaymentMethods
 * @param {object[]} input.passengers — form passengers (roles + DOB)
 * @param {object} input.appliedSearch — rooms + childAges occupancy
 * @param {object} input.customer — booker customer node
 * @param {object} input.reference — { agency, voucherEmail? }
 * @param {string} [input.paymentMethodName="account_credit"]
 * @param {number} [input.paxFallback=2]
 * @param {string} [input.bookingDate] — UTC calendar date of this Book request
 * @param {Date} [input.now] — used only when bookingDate is omitted
 */
export function buildHubwayzOfficialBookPayload(input = {}) {
  const offerCode = String(input.offerCode || "").trim();
  const token = String(input.token || "").trim();
  const searchRequest = input.searchRequest;
  const prebookNormalized = input.prebookNormalized;
  const paymentMethodName = String(
    input.paymentMethodName || "account_credit"
  ).trim();

  if (!token) {
    return fail("missing_token", "Pre-Book token is required");
  }
  if (!offerCode) {
    return fail("missing_offer_code", "Search/poll offer code is required");
  }
  if (!searchRequest || typeof searchRequest !== "object") {
    return fail("missing_search_request", "Original searchRequest is required");
  }
  if (!prebookNormalized?.supplierConfirmed) {
    return fail(
      "prebook_not_confirmed",
      "Pre-Book is stale or missing critical supplier fields",
      { criticalMissing: prebookNormalized?.criticalMissing || null }
    );
  }
  const prebookToken = String(prebookNormalized?.display?.token || "").trim();
  if (!prebookToken || prebookToken !== token) {
    return fail(
      "token_mismatch",
      "Book token must match supplier-confirmed Pre-Book token",
      { prebookToken: prebookToken || null }
    );
  }

  if (!SUPPORTED_PAYMENT_METHODS.has(paymentMethodName)) {
    return fail(
      "unsupported_payment_method",
      `paymentMethod '${paymentMethodName}' is not in the documented set`,
      { allowed: [...SUPPORTED_PAYMENT_METHODS] }
    );
  }

  const enabledMethods = extractEnabledPaymentMethods(input.prebookRaw);
  const requirePm = input.requirePaymentMethodsFromPrebook !== false;
  if (enabledMethods == null) {
    if (requirePm) {
      return fail(
        "payment_methods_unverified",
        "Pre-Book PaymentMethods missing; cannot verify paymentMethod mapping"
      );
    }
  } else if (!enabledMethods.includes(paymentMethodName)) {
    return fail(
      "payment_method_not_enabled",
      `paymentMethod '${paymentMethodName}' is not enabled on Pre-Book PaymentMethods`,
      { enabledMethods }
    );
  }

  const agency = String(input.reference?.agency || "").trim();
  if (!agency) {
    return fail("missing_agency_reference", "reference.agency is required");
  }

  const customer = input.customer;
  if (!customer?.name?.first || !customer?.name?.last) {
    return fail("missing_customer", "Top-level customer name is required");
  }

  const customerContactBuilt = buildHubwayzCustomerContact(customer.contact || {});
  if (!customerContactBuilt.ok) {
    const err = customerContactBuilt.error;
    return fail(err.code, err.message, err.details || null);
  }

  // customer.birthDate is required on the booking customer, not on each adult guest.
  // Hotels-only does not ask for it. An explicit ISO date is kept (flight+hotel).
  // Otherwise age 30 is computed from check-in, or from today when check-in is missing.
  const explicitBirth = String(customer.birthDate || "").trim();
  let customerBirthDate;
  if (!explicitBirth) {
    customerBirthDate = derivedCustomerBirthDate(searchRequest?.dates?.from);
  } else if (!ISO_DATE.test(explicitBirth)) {
    return fail(
      "invalid_customer_birthDate",
      "customer.birthDate must be YYYY-MM-DD"
    );
  } else {
    customerBirthDate = explicitBirth;
  }
  const searchCountry = String(searchRequest.customerCountry || "")
    .trim()
    .toUpperCase();
  const contactCountry = String(customerContactBuilt.contact.country || "")
    .trim()
    .toUpperCase();
  if (searchCountry && contactCountry !== searchCountry) {
    return fail(
      "customer_country_mismatch",
      "customer.contact.country must match searchRequest.customerCountry",
      { field: "customer.contact.country" }
    );
  }

  const prebookRooms = prebookNormalized?.offers?.[0]?.rooms || [];
  if (!prebookRooms.length) {
    return fail("missing_prebook_rooms", "Pre-Book has no room items");
  }

  const assignment = assignPassengersToRooms(
    input.passengers,
    input.appliedSearch,
    input.paxFallback,
    searchRequest
  );
  if (assignment.leftoverAdults.length || assignment.leftoverChildren.length) {
    return fail(
      "guest_composition_mismatch",
      "Passenger counts do not match room occupancy",
      {
        leftoverAdults: assignment.leftoverAdults.length,
        leftoverChildren: assignment.leftoverChildren.length,
      }
    );
  }
  if (assignment.rooms.length !== prebookRooms.length) {
    return fail(
      "room_count_mismatch",
      "Occupancy room count does not match Pre-Book items",
      {
        occupancyRooms: assignment.rooms.length,
        prebookRooms: prebookRooms.length,
      }
    );
  }

  const requestedForMap = assignment.rooms.map((bucket) => ({
    adults: bucket.expectedAdults,
    childAges: bucket.expectedChildAges,
    roomId: bucket.roomId,
  }));
  const unresolvedChildAge = requestedForMap.some((room) =>
    normalizedAgeList(room.childAges).some((age) => !Number.isFinite(Number(age)))
  );
  const mapping = unresolvedChildAge
    ? {
        ok: true,
        supplierToRequested: requestedForMap.map((_, index) => index),
      }
    : mapRequestedRoomsToSupplierRooms(requestedForMap, prebookRooms);
  if (!mapping.ok) {
    return fail(mapping.code, mapping.message, mapping.details || null);
  }

  const paxRooms = [];
  let leadAssigned = false;
  const hasExplicitPrimary = assignment.rooms.some((bucket) =>
    bucket.adults.some((adult) => adult?.isPrimary)
  );

  for (let i = 0; i < prebookRooms.length; i++) {
    const requestedIndex = mapping.supplierToRequested[i];
    const bucket = assignment.rooms[requestedIndex];
    const pbRoom = prebookRooms[i];
    const pbAdults = Number(pbRoom?.pax?.adults);
    const prebookAdultsKnown = !!(pbRoom?.pax && pbAdults > 0);
    if (
      prebookAdultsKnown &&
      pbAdults !== bucket.adults.length &&
      bucket.adults.length > pbAdults
    ) {
      return fail(
        "prebook_occupancy_mismatch",
        `Room ${i + 1}: Pre-Book adults ${pbAdults} ≠ assigned ${bucket.adults.length}`
      );
    }
    if (bucket.adults.length !== bucket.expectedAdults) {
      return fail(
        "guest_composition_mismatch",
        `Room ${i + 1}: expected ${bucket.expectedAdults} adults, got ${bucket.adults.length}`
      );
    }
    if (bucket.children.length !== bucket.expectedChildren) {
      return fail(
        "guest_composition_mismatch",
        `Room ${i + 1}: expected ${bucket.expectedChildren} children, got ${bucket.children.length}`
      );
    }
    if (prebookAdultsKnown && pbAdults !== bucket.adults.length) {
      return fail(
        "prebook_occupancy_mismatch",
        `Room ${i + 1}: Pre-Book adults ${pbAdults} ≠ assigned ${bucket.adults.length}`
      );
    }

    const adultsOut = [];
    for (let a = 0; a < bucket.adults.length; a++) {
      const isLead =
        !leadAssigned &&
        (hasExplicitPrimary ? !!bucket.adults[a].isPrimary : i === 0 && a === 0);
      const guest = asAdultGuest(bucket.adults[a], {
        lead: isLead,
        includeContact: isLead,
      });
      if (!guest) {
        return fail(
          "invalid_adult_guest",
          `Room ${i + 1}: adult missing first/last name`
        );
      }
      if (guest.__invalid) {
        return fail(
          guest.reason || "missing_lead_contact_phone",
          `Room ${i + 1}: lead adult contact.phone required — ${guest.message || ""}`
        );
      }
      if (guest.lead) leadAssigned = true;
      adultsOut.push(guest);
    }

    const bookingDate = resolveBookingDate(input);
    const supplierAges = Array.isArray(pbRoom?.pax?.childAges)
      ? pbRoom.pax.childAges
      : [];
    const childSlots = orderChildrenBySupplierAges(
      bucket.children,
      bucket.expectedChildAges || [],
      supplierAges
    );
    const childrenOut = [];
    if (bucket.children.length && !ISO_DATE.test(bookingDate)) {
      return fail(
        "missing_booking_date",
        `חדר ${i + 1}: חסר תאריך הזמנה לחישוב גיל הילד`
      );
    }
    const childSlotsOrRequest =
      childSlots ||
      bucket.children.map((child, childIndex) => ({
        child,
        expected: (bucket.expectedChildAges || [])[childIndex],
      }));
    if (bucket.children.length && !childSlots && !unresolvedChildAge) {
      return fail(
        "room_occupancy_map_impossible",
        `Room ${i + 1}: child ages do not match the supplier room`,
        { roomIndex: i, requestedIndex }
      );
    }
    for (let c = 0; c < childSlotsOrRequest.length; c++) {
      const child = childSlotsOrRequest[c].child;
      const expected = Number(childSlotsOrRequest[c].expected);
      const supplied = suppliedChildBirthIso(child);
      let birthDate = supplied;
      if (supplied) {
        const suppliedAge = ageAtIsoDate(supplied, bookingDate);
        if (suppliedAge !== expected) {
          return fail(
            "child_age_mismatch",
            `חדר ${i + 1}: גיל הילד בתאריך ההזמנה אינו ${expected}. נדרש חיפוש חדש.`,
            {
              roomIndex: i,
              childIndex: c,
              expectedAge: expected,
              actualAge: suppliedAge,
              requiresNewSearch: true,
            }
          );
        }
      } else {
        birthDate = derivedChildBirthDate(bookingDate, expected);
        if (!birthDate) {
          return fail(
            "missing_child_search_age",
            `חדר ${i + 1}: חסר גיל ילד מהחיפוש. לא נשלח תאריך.`,
            { roomIndex: i, childIndex: c }
          );
        }
      }
      const mapped = asChildGuest(child, birthDate);
      if (!mapped.ok) {
        return fail(
          mapped.reason === "child_name" ? "invalid_child_guest" : mapped.reason,
          `חדר ${i + 1}: לילד חסר שם`,
          { passengerId: child.id || null }
        );
      }
      const age = ageAtIsoDate(mapped.guest.birthDate, bookingDate);
      if (age !== expected) {
        return fail(
          "child_age_mismatch",
          `חדר ${i + 1}: גיל הילד בתאריך ההזמנה אינו ${expected}. נדרש חיפוש חדש.`,
          {
            roomIndex: i,
            childIndex: c,
            expectedAge: expected,
            actualAge: age,
            requiresNewSearch: true,
          }
        );
      }
      childrenOut.push(mapped.guest);
    }

    paxRooms.push({
      adults: adultsOut,
      children: childrenOut,
    });
  }

  if (!leadAssigned) {
    return fail("missing_lead", "Exactly one lead adult is required");
  }

  // One booking unit per Pre-Book hotel offer (shared token) with pax[] per room.
  // Docs: bookingRequest[].pax is an array of rooms under one code+token.
  const bookingRequest = [
    {
      code: offerCode,
      token,
      pax: paxRooms,
    },
  ];

  const payload = {
    customer: {
      title:
        String(customer.title || "M").trim().toUpperCase() === "F" ? "F" : "M",
      name: { ...customer.name },
      contact: customerContactBuilt.contact,
      ...(customerBirthDate ? { birthDate: customerBirthDate } : {}),
    },
    paymentMethod: {
      methodName: paymentMethodName,
    },
    reference: {
      agency,
      ...(input.reference?.voucherEmail
        ? { voucherEmail: String(input.reference.voucherEmail).trim() }
        : {}),
    },
    services: [
      {
        searchRequest,
        bookingRequest,
      },
    ],
  };

  return ok(payload, {
    roomsBooked: paxRooms.length,
    paymentMethodName,
    offerCode,
    tokenPresent: true,
    roomOccupancyMap: mapping.supplierToRequested,
  });
}
